import { useLayoutEffect, type RefObject } from "react";

/**
 * Keeps a text field pinned to its last line while its value is written
 * programmatically.
 *
 * A browser only scrolls a field to follow the caret when the user types.
 * Live dictation sets the value from outside, so once the transcript grows past
 * the visible height the newly written words land below the fold and the user
 * watches a frozen block of text. This scrolls to the end after every update.
 *
 * Runs in a layout effect so the scroll lands in the same frame as the new text
 * — in a plain effect the field would visibly jump.
 *
 * @param ref - The field to keep scrolled.
 * @param value - The text being written; each change triggers a scroll.
 * @param isActive - Only scroll while text is arriving, so the user stays free
 * to scroll back once dictation has stopped.
 */
export const useScrollToBottom = (
  ref: RefObject<HTMLTextAreaElement | HTMLInputElement | null>,
  value: string,
  isActive: boolean,
): void => {
  useLayoutEffect(() => {
    if (!isActive) {
      return;
    }
    const element = ref.current;
    if (!element) {
      return;
    }
    // Textareas overflow vertically, single-line inputs horizontally.
    element.scrollTop = element.scrollHeight;
    element.scrollLeft = element.scrollWidth;
  }, [ref, value, isActive]);
};
